/**
 * Bounded fan-out for `markProjectOnboarded`.
 *
 * The schema only takes one id per call (`markProjectOnboarded(id: ID!)`), so a
 * bulk acknowledgement needs one request per project — but issuing all of them
 * at once turns a partial failure into an unreadable burst, and each call also
 * produces a `meUpdated` event back.
 *
 * Lives in `utils` rather than next to its first caller: the member-added toast
 * acknowledges the same way (TA-10856 QA), and importing the onboarding hook
 * for it would drag `authContext` — and with it `expo-constants` — into a test
 * environment that has no reason to boot either.
 */

/** Most markProjectOnboarded requests "Dismiss All" may have in flight at once. */
export const ONBOARDING_DISMISS_CONCURRENCY = 4;

/**
 * Runs `dismiss` over `projectIds` in queue order with at most `concurrency`
 * requests in flight at a time.
 *
 * Never rejects: `dismiss` reports its own per-project failures, and a project
 * whose request failed simply reappears on the next unviewed list.
 */
export const dismissWithConcurrency = async ({
  projectIds,
  dismiss,
  concurrency = ONBOARDING_DISMISS_CONCURRENCY,
}: {
  projectIds: string[];
  dismiss: (projectId: string) => Promise<unknown>;
  concurrency?: number;
}): Promise<void> => {
  const pending = [...projectIds];
  const worker = async (): Promise<void> => {
    for (let id = pending.shift(); id !== undefined; id = pending.shift()) {
      // Sequential inside each worker on purpose: this is what caps the fan-out.

      await dismiss(id).catch(() => {});
    }
  };
  const workers = Math.min(Math.max(concurrency, 1), pending.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
};
